import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import EgovexLogo from '../components/EgovexLogo';
import { trackPageView, trackSignUp } from '../src/lib/analytics';
import { LANDING_STRINGS, LandingLanguage } from '../constants/landingTranslations';

interface RegisterViewProps {
  onBack: () => void;
  onLogin: () => void;
  onSuccess: () => void;
  lang?: LandingLanguage;
}

const RegisterView: React.FC<RegisterViewProps> = ({ onBack, onLogin, onSuccess, lang = 'en' }) => {
  const t = LANDING_STRINGS[lang].auth;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    trackPageView('/register', 'Register', false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }

    if (password.length < 8) {
      setError(t.passwordTooShort);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, firstName, lastName }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || t.registerError);
        return;
      }

      trackSignUp('email');
      onSuccess();
    } catch (err) {
      setError(t.connectionError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    trackSignUp('google');
    window.location.href = '/api/auth/google';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <style>{`
        .glass { 
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
      `}</style>

      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-black/[0.04]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <button onClick={onBack} className="flex items-center gap-2 text-black/60 hover:text-black transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{t.back}</span>
            </button>
            <div className="flex items-center gap-3">
              <EgovexLogo size={36} />
              <span className="font-black text-xl tracking-tighter">Egovex</span>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 pt-24 pb-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-[#1D1D1F] mb-2">{t.registerTitle}</h1>
            <p className="text-black/50">{t.registerSubtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-2xl text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <div className="relative flex-1">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/30" />
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t.firstName}
                  className="w-full pl-12 pr-4 py-4 bg-black/[0.03] border border-black/[0.06] rounded-2xl text-[#1D1D1F] placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-black/10 transition-all"
                />
              </div>
              <div className="relative flex-1">
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t.lastName}
                  className="w-full px-4 py-4 bg-black/[0.03] border border-black/[0.06] rounded-2xl text-[#1D1D1F] placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-black/10 transition-all"
                />
              </div>
            </div>

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/30" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.email}
                required
                className="w-full pl-12 pr-4 py-4 bg-black/[0.03] border border-black/[0.06] rounded-2xl text-[#1D1D1F] placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-black/10 transition-all"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/30" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.password}
                required
                className="w-full pl-12 pr-12 py-4 bg-black/[0.03] border border-black/[0.06] rounded-2xl text-[#1D1D1F] placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-black/10 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-black/30 hover:text-black/50 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-black/30" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t.confirmPassword}
                required
                className="w-full pl-12 pr-4 py-4 bg-black/[0.03] border border-black/[0.06] rounded-2xl text-[#1D1D1F] placeholder-black/30 focus:outline-none focus:ring-2 focus:ring-black/10 transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-[#1D1D1F] text-white font-bold rounded-2xl hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '...' : t.registerButton}
            </button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-black/10" />
            <span className="text-black/40 text-sm">{t.orContinueWith}</span>
            <div className="flex-1 h-px bg-black/10" />
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full py-4 bg-white border-2 border-black/10 text-[#1D1D1F] font-bold rounded-2xl hover:border-black/20 hover:bg-black/[0.02] transition-all flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t.google}
          </button>

          <p className="text-center mt-8 text-black/50">
            {t.haveAccount}{' '}
            <button onClick={onLogin} className="text-[#1D1D1F] font-bold hover:underline">
              {t.signIn}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterView;
